import React, { memo, useCallback } from "react";
import colors from "app/colors";

import { TrashOutlineIcon } from "modules/icons";
import {
  AdminEditDistributionGroupMutation,
  AdminEditDistributionGroupMutationVariables,
  DistributionGroupFragment,
  DistributionGroupListQuery,
} from "gql/graphql";
import { useApolloClient, useMutation } from "@apollo/client";
import { Button } from "@chakra-ui/react";
import DISTRIBUTION_GROUP_LIST_QUERY from "modules/providers/distribution-groups-provider/networking/distribution-group-list";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION from "../_networking/admin-edit-distribution-group";

interface IProps {
  distributionGroup?: DistributionGroupFragment;
  onDelete?: () => void;
}

const AdminDistributionGroupDeleteQueryButton = ({
  distributionGroup,
  onDelete,
}: IProps) => {
  const { cache } = useApolloClient();
  const { openModal } = useOverlay();
  const [adminEditDistributionGroupMutationAPI] = useMutation<
    AdminEditDistributionGroupMutation,
    AdminEditDistributionGroupMutationVariables
  >(ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION);

  const deleteDynamicRules = useCallback(() => {
    if (!distributionGroup) return;
    adminEditDistributionGroupMutationAPI({
      variables: {
        id: distributionGroup.id,
        input: {
          query: null,
          queryBuilder: null,
        },
      },
      onCompleted: (data) => {
        const updatedDistributionGroup = data.adminEditDistributionGroup;
        if (!updatedDistributionGroup) {
          return;
        }
        if (onDelete) onDelete();
        cache.updateQuery<DistributionGroupListQuery>(
          {
            query: DISTRIBUTION_GROUP_LIST_QUERY,
          },
          (data) => {
            if (!data) {
              return data;
            }
            return {
              ...data,
              distributionGroupList: data.distributionGroupList.map((group) =>
                group.id === updatedDistributionGroup.id
                  ? {
                      ...updatedDistributionGroup,
                      query: null,
                      queryBuilder: null,
                    }
                  : group,
              ),
            };
          },
        );
      },
    });
  }, [
    adminEditDistributionGroupMutationAPI,
    cache,
    distributionGroup,
    onDelete,
  ]);

  const onClick = useCallback(() => {
    if (!distributionGroup) return;
    openModal({
      type: MODAL_TYPE.CONFIRM,
      onSaveCallback: deleteDynamicRules,
      helpModal: {
        icon: <TrashOutlineIcon boxSize={14} fill={colors.black} />,
        title: "Delete Dynamic Rules",
        message:
          "Are you sure you want to delete dynamic rules for this group? This action cannot be undone.",
      },
    });
  }, [deleteDynamicRules, distributionGroup, openModal]);

  if (!distributionGroup || distributionGroup.query === null) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      alignItems="center"
      fontSize={12}
      fontWeight={600}
      color={colors.gray40}
      size="xs"
      leftIcon={<TrashOutlineIcon boxSize={4} fill={colors.gray40} />}
      onClick={onClick}
    >
      Delete Rules
    </Button>
  );
};

export default memo(AdminDistributionGroupDeleteQueryButton);
